export interface TransitionalPollOptions {
  /** Performs one refresh; true means the refresh succeeded. */
  request: () => Promise<boolean>;
  /** Base interval of a healthy refresh. */
  intervalMs: number;
  /** Upper bound of the backoff after failed refreshes. */
  maxIntervalMs?: number;
  /** Visibility source; the document state by default. */
  isVisible?: () => boolean;
  /** Subscribes to visibility changes; the document by default. */
  subscribeVisibility?: (listener: () => void) => () => void;
}

function defaultIsVisible(): boolean {
  if (typeof document === "undefined") {
    return true;
  }
  return document.visibilityState !== "hidden";
}

function defaultSubscribeVisibility(listener: () => void): () => void {
  if (typeof document === "undefined") {
    return () => {};
  }
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
}

/**
 * Transitional polling for pages that still refresh over REST while a server is
 * in a transitional state: one request at a time, jittered cadence, backoff after
 * failures, and no requests while the tab is hidden. The page keeps ownership of
 * its data; the poll only decides when a refresh may happen.
 */
export function startTransitionalPoll(options: TransitionalPollOptions): {
  refresh(): void;
  stop(): void;
} {
  const isVisible = options.isVisible ?? defaultIsVisible;
  const subscribeVisibility = options.subscribeVisibility ?? defaultSubscribeVisibility;
  const maxIntervalMs = options.maxIntervalMs ?? options.intervalMs * 8;
  let stopped = false;
  let inFlight = false;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = null;
  };
  const schedule = () => {
    clear();
    if (stopped || inFlight || !isVisible()) {
      return;
    }
    const base = Math.min(maxIntervalMs, options.intervalMs * 2 ** Math.min(failures, 5));
    const jitter = Math.floor(base * 0.2 * Math.random());
    timer = setTimeout(() => void run(), base + jitter);
  };
  const run = async () => {
    clear();
    if (stopped || !isVisible()) {
      schedule();
      return;
    }
    if (inFlight) {
      schedule();
      return;
    }
    inFlight = true;
    try {
      failures = (await options.request()) ? 0 : failures + 1;
    } catch {
      failures++;
    } finally {
      inFlight = false;
      if (!stopped) {
        schedule();
      }
    }
  };
  const unsubscribe = subscribeVisibility(() => {
    if (stopped) {
      return;
    }
    if (isVisible()) {
      void run();
    } else {
      clear();
    }
  });
  schedule();
  return {
    refresh() {
      void run();
    },
    stop() {
      stopped = true;
      clear();
      unsubscribe();
    },
  };
}
